<template>
  <div>
    <!-- Encabezado -->
    <base-header class="pb-6 pb-1 pt-5 pt-md-8 bg-gradient-custom" type="custom">
    </base-header>

    <!-- Mostrar Tabla -->
    <b-container fluid class="mt--4">
      <ReusableTable
        :title="tableTitle"
        :data="dataSims"
        :columns="columns"
        :per-page="itemsPerPage"
        :total="totalSims"
        :search-query="searchQuery"
        @fetch-data="cargarDatosSims"
        @search="onSearch"
      >
        <template
          v-for="columna in columns"
          v-slot:[`column-${columna.prop}`]="{ row }"
        >
          <span :key="columna.prop">
            <span v-if="columna.esAttData">
              {{ obtenerValor(row, columna.prop) }}
            </span>
            <span v-else-if="columna.prop === 'DATA'">
              {{ convertirUnidades(row[columna.prop]) }}
            </span>
            <span
              v-else-if="
                columna.prop === 'FECHA_ACTIVACION' ||
                columna.prop === 'FECHA_CARGA_DATA'
              "
            >
              {{ formatearFechaParaDatetimeLocal(new Date(row[columna.prop])) }}
            </span>
            <span v-else>
              {{ row[columna.prop] }}
            </span>
          </span>
        </template>
      </ReusableTable>
    </b-container>

    <!-- Modal de sesión expirada -->
    <TokenExpiredModal :show="showTokenExpiredModal"> </TokenExpiredModal>

    <!-- Modal de carga de CSV -->
    <CsvHandler ref="csvHandler" @csv-uploaded="cargarDatosSims" />
  </div>
</template>
  
  <script>
import ReusableTable from "./Tables/RegularTables/TablaReu.vue";
import axios from "axios";
import Modal from "../components/Modal.vue";
import CsvHandler from "../components/CsvHandler.vue";
import TokenExpiredModal from "../components/TokenExpiredModal.vue";

export default {
  components: { ReusableTable, Modal, CsvHandler, TokenExpiredModal },
  data() {
    return {
      dataSims: [], // Datos de la tabla
      showDeleteModal: false, // Controla la visibilidad del modal de eliminación
      simToDelete: null, // SIM seleccionado para eliminar
      isLoading: true, // Estado de carga
      showTokenExpiredModal: false, // Controla la visibilidad del modal de token expirado
      currentPage: 1, // Página actual
      itemsPerPage: 10, // Elementos por página
      totalSims: 0, // Total de SIMs
      searchQuery: "", // Consulta de búsqueda
      tableTitle: "Listado de Sims", // Título de la tabla
      columns: [
        { prop: "ICCID", label: "ICCID", minWidth: "210" },
        { prop: "PLAN", label: "Plan", minWidth: "120" },
        { prop: "NAME_ORG", label: "Nombre Org", minWidth: "150" },
        { prop: "LABEL", label: "LABEL", minWidth: "128" },
        { prop: "PRIMARY_MSISDN", label: "MSISDN", minWidth: "180" },
        { prop: "SIM_STATUS", label: "Estado SIM", minWidth: "150" },
        { prop: "DATA_STATUS", label: "Estado Datos", minWidth: "125" },
        { prop: "DATA", label: "Datos", minWidth: "130" },
        {
          prop: "FECHA_ACTIVACION",
          label: "Fecha Activación",
          minWidth: "200",
        },
        {
          prop: "FECHA_CARGA_DATA",
          label: "Fecha Carga Datos",
          minWidth: "200",
        },
        {
          prop: "Session_Status",
          label: "Estado Sesión",
          minWidth: "120",
          esAttData: true,
        },
        { prop: "Sms_Mo", label: "SMS MO", minWidth: "140", esAttData: true },
        { prop: "Sms_Mt", label: "SMS MT", minWidth: "140", esAttData: true },
        { prop: "Country", label: "País", minWidth: "140", esAttData: true },
        { prop: "Network", label: "Red", minWidth: "120", esAttData: true },
        { prop: "Tags", label: "Tags", minWidth: "100", esAttData: true },
        {
          prop: "Ip_Address",
          label: "Dirección IP",
          minWidth: "160",
          esAttData: true,
        },
        {
          prop: "Device_Imei",
          label: "IMEI Dispositivo",
          minWidth: "180",
          esAttData: true,
        },
      ], // Columnas de la tabla
      actions: [
        { label: "Editar", onClick: this.editarSim },
        { label: "Eliminar", onClick: this.eliminarSim },
      ], // Acciones de la tabla
    };
  },
  async mounted() {
    await this.cargarDatosSims(); // Cargar datos al montar el componente
  },
  watch: {
    // Observar cambios en la página actual
    currentPage() {
      this.cargarDatosSims();
    },
    // Observar cambios en la consulta de búsqueda
    searchQuery() {
      this.currentPage = 1; // Reiniciar a la primera página al buscar
      this.cargarDatosSims();
    },
  },
  methods: {
    // Obtener la lista de SIMs
    openCsvModal() {
      this.$refs.csvHandler.openModal(); // Abrir el modal de carga de CSV
    },
    async cargarDatosSims({ page, perPage, search } = {}) {
      this.isLoading = true;
      const token = localStorage.getItem("token");

      if (!token) {
        this.handleTokenExpired(); // Mostrar modal y redirigir al login
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/leerDatosSIMUC`,
          {
            params: {
              page: page || this.currentPage,
              perPage: perPage || this.itemsPerPage,
              search: search || this.searchQuery,
            },
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          // Asegúrate de que response.data.datos sea un array
          this.dataSims = Array.isArray(response.data.datos)
            ? response.data.datos
            : [];
          this.totalSims = response.data.total || 0;
        } else {
          this.mostrarError("Error al cargar los SIMs.");
          this.dataSims = []; // Asignar un array vacío en caso de error
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          // Si el token expiró, manejar el token expirado
          this.handleTokenExpired();
        } else {
          this.mostrarError("Error en la solicitud: " + error.message);
        }
        this.dataSims = []; // Asignar un array vacío en caso de error
      } finally {
        this.isLoading = false;
      }
    },
    // Método para manejar el token expirado
    handleTokenExpired() {
      this.showTokenExpiredModal = true; // Mostrar el modal

      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        this.$router.push("/login"); // Redirige al login
        localStorage.removeItem("token"); // Elimina el token del localStorage
      }, 3000); // 3000 ms = 3 segundos
    },

    // Mostrar mensaje de éxito
    mostrarExito(mensaje) {
      alert(`Éxito: ${mensaje}`);
    },
    // Mostrar mensaje de error
    mostrarError(mensaje) {
      alert(`Error: ${mensaje}`);
    },
    // Manejar la búsqueda
    onSearch(searchQuery) {
      this.searchQuery = searchQuery;
      this.cargarDatosSims();
    },
    // Obtener valor de ATT_DATA
    obtenerValor(objeto, ruta) {
      try {
        const attDataString = objeto.ATT_DATA;
        const attDataObject = JSON.parse(attDataString);
        return attDataObject[ruta];
      } catch (error) {
        console.error("Error al obtener valor:", error);
        return "";
      }
    },
    // Convertir unidades de datos
    convertirUnidades(valor) {
      const val = parseFloat(valor);
      const kilobit = val / 1000;
      const megabit = kilobit / 1000;
      const gigabit = megabit / 1000;

      let resultado = "";

      if (gigabit > 1) {
        resultado = gigabit.toFixed(2) + " GB";
      } else if (megabit > 1) {
        resultado = megabit.toFixed(2) + " MB";
      } else if (kilobit > 1) {
        resultado = kilobit.toFixed(2) + " KB";
      } else if (val > 1) {
        resultado = val.toFixed(2) + " bits";
      }

      return resultado;
    },
    // Formatear fecha para datetime-local
    formatearFechaParaDatetimeLocal(fecha) {
      const formatoParaDatetimeLocal =
        fecha.getFullYear() +
        "-" +
        ("0" + (fecha.getMonth() + 1)).slice(-2) +
        "-" +
        ("0" + fecha.getDate()).slice(-2) +
        "T" +
        ("0" + fecha.getHours()).slice(-2) +
        ":" +
        ("0" + fecha.getMinutes()).slice(-2);

      return formatoParaDatetimeLocal;
    },
  },
};
</script> 